import React from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import Switch from "react-switch";
import Input from "components/input";
import Select from "components/select";
import UploadFile from "components/uploadFile";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import { useSelector } from "react-redux";
import { useLookupsContext } from "hooks/useContext";
import useUploadFiles from "pages/signup/hooks/useUploadFiles";
import { vatCertificationType } from "utils/constantValues";

function EditVatInformation({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const { countries = [] } = useLookupsContext(false);

  const vatCertification = userDetails?.userFiles?.find(
    (file) => file.type === vatCertificationType
  );
  const defaultsValues = {
    isVatExempt: userDetails?.flags?.isVatExempt ?? false,
    vatCountryId: userDetails?.vatCountry?.id ?? null,
    vatRegistrationNumber: userDetails?.vatRegistrationNumber ?? "",
    vatCertification: vatCertification?.id ?? "",
  };

  const countriesOptions = countries.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: defaultsValues });

  const {
    vatRef,
    isUploading,
    vatFileName,
    uploadVatCertificate,
    removeVatCertificate,
  } = useUploadFiles({ defaultVatFile: vatCertification });

  isLoading = isUploading.vatCertification || isLoading;
  const isVatExempt = watch("isVatExempt");

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("vatExempt")}
        </div>
        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="grid gap-[30px] w-full p-[24px]">
        <div className="flex flex-col gap-[2px]">
          <div className="text-[#1D1B22] font-medium text-[16px]">
            {t("vatExempt")}
          </div>
          <Switch
            checked={!!isVatExempt}
            onChange={(checked) => setValue("isVatExempt", checked)}
            onColor="#023A66"
            handleDiameter={16}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            height={20}
            width={40}
            className="react-switch"
          />
        </div>

        {!isVatExempt && (
          <div className="flex flex-wrap gap-[20px]">
            <Select
              label={t("vatCountry")}
              placeholder={t("placeholderSelect", {
                field: t("vatCountry").toLowerCase(),
              })}
              name={"vatCountryId"}
              isClearable={false}
              containerClassName="flex flex-1 flex-col gap-[10px] min-w-[250px] sm-max:w-full"
              value={
                watch("vatCountryId")
                  ? countriesOptions.find(
                      (elem) => elem.value === watch("vatCountryId")
                    )
                  : null
              }
              onChange={(e) => setValue("vatCountryId", e.value)}
              isRequired={true}
              register={register}
              registerArgs={{
                required: t("errorField", { field: t("vatCountry") }),
              }}
              errorMessage={
                errors.vatCountryId ? errors.vatCountryId.message : ""
              }
              options={countriesOptions}
            />

            <Input
              label={t("vatRegistrationNumber")}
              placeholder={t("placeHolderType", {
                title: t("vatRegistrationNumber").toLowerCase(),
              })}
              type="string"
              name="vatRegistrationNumber"
              register={register}
              isRequired={true}
              registerArgs={{
                required: t("errorField", {
                  field: t("vatRegistrationNumber"),
                }),
              }}
              errorMessage={
                errors.vatRegistrationNumber
                  ? errors.vatRegistrationNumber.message
                  : ""
              }
              containerClassName="flex flex-col flex-1 sm-max:grid sm-max:w-full gap-[10px]"
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[40px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                errors.vatRegistrationNumber
                  ? "border-red"
                  : "border-default-color"
              } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
            />

            <UploadFile
              name={"vatCertification"}
              register={register}
              registerArgs={{
                required: vatFileName
                  ? false
                  : t("errorField", { field: t("vatCertification") }),
              }}
              label={t("vatCertification")}
              placeHolderLabel={t("attachUrVat")}
              icon={<AttachIcon />}
              isUploading={isUploading.vatCertification}
              getFile={(e, isDrag) => uploadVatCertificate(e, isDrag, setValue)}
              containerClassName="grid w-full gap-[10px] flex-1 min-w-[250px]"
              labelClassName="text-primary font-semibold text-[16px]"
              placeHolderContainerClassName={`flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[40px] w-full min-w-[190px] sm-max:min-w-full sm-max:w-full border-[1px] border-dashed ${
                errors.vatCertification && !vatFileName
                  ? "border-red"
                  : "border-dark-gray"
              } cursor-pointer`}
              isRequired={true}
              errorMessage={
                errors.vatCertification
                  ? errors.vatCertification.message
                  : ""
              }
              ref={vatRef}
              fileName={vatFileName}
              onRemoveFile={(fileName) =>
                removeVatCertificate(fileName, setValue)
              }
            />
          </div>
        )}
      </div>
    </form>
  );
}

EditVatInformation.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditVatInformation;
